import { useEffect, useState, forwardRef } from 'react';
import { OnboardingProvider, useOnboarding } from './OnboardingContext';
import { ProgressIndicator } from './ProgressIndicator';
import { BackButton } from './BackButton';
import { LanguageSelector } from './LanguageSelector';
import { useLanguageDetection } from '@/hooks/useLanguageDetection';
import { useOnboardingSessionTimeout } from '@/hooks/useOnboardingSessionTimeout';
import SessionTimeoutCountdown from '@/components/SessionTimeoutCountdown';
import { WelcomeStep } from './steps/WelcomeStep';
import { AccountTypeStep } from './steps/AccountTypeStep';
import { PhoneStep } from './steps/PhoneStep';
import { IndividualDeclarationsStep } from './steps/IndividualDeclarationsStep';
import { EmailStep } from './steps/EmailStep';
import { PersonalInfoStep } from './steps/PersonalInfoStep';
import { DateOfBirthStep } from './steps/DateOfBirthStep';
import { EmploymentStep } from './steps/EmploymentStep';
import { IncomeStep } from './steps/IncomeStep';
import { PaymentDestinationsStep } from './steps/PaymentDestinationsStep';
import { DocumentUploadStep } from './steps/DocumentUploadStep';
import { DocumentDetailsConfirmationStep } from './steps/DocumentDetailsConfirmationStep';
import { DocumentSigningStep } from './steps/DocumentSigningStep';
import { PasscodeStep } from './steps/PasscodeStep';
import { FinalDeclarationsStep } from './steps/FinalDeclarationsStep';
import { SuccessStep } from './steps/SuccessStep';
// Corporate steps
import { CorporateCompanySearchStep } from './steps/CorporateCompanySearchStep';
import { CorporateCompanyDetailsStep } from './steps/CorporateCompanyDetailsStep';
import { CorporateBusinessActivityStep } from './steps/CorporateBusinessActivityStep';
import { CorporatePaymentFlowsStep } from './steps/CorporatePaymentFlowsStep';
import { CorporateDirectorsShareholdersStep } from './steps/CorporateDirectorsShareholdersStep';
import { CorporateUBOTraceStep } from './steps/CorporateUBOTraceStep';
import { CorporateBiometricsStep } from './steps/CorporateBiometricsStep';
import { CorporateServicesStep } from './steps/CorporateServicesStep';
import { CorporateBaaSStep } from './steps/CorporateBaaSStep';
import { Clock, LogOut } from 'lucide-react';
import { OnboardingNotificationBell } from './OnboardingNotificationBell';
import { OnboardingSupportPanel } from './OnboardingSupportPanel';
import { HelpCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

// Steps before passcode — no timeout yet (profile not created)
const PRE_PASSCODE_STEPS = new Set([
  'welcome', 'account-type',
  'phone', 'individual-declarations', 'email',
  'corporate-company-search', 'corporate-company-details', 'corporate-services',
  'corporate-phone', 'corporate-email',
]);

const OnboardingContent = forwardRef<HTMLDivElement>(function OnboardingContent(_props, ref) {
  const { currentStep, duplicateBlock, clearDuplicateBlock } = useOnboarding();
  const [supportOpen, setSupportOpen] = useState(false);

  // Auto-detect language from IP on first visit
  useLanguageDetection();

  // Timeout activates only after passcode step (profile exists in DB)
  const timeoutActive = !PRE_PASSCODE_STEPS.has(currentStep) &&
    currentStep !== 'success' && currentStep !== 'corporate-success';

  const { showCountdown, countdownSeconds, resetTimer, timedOut } =
    useOnboardingSessionTimeout(timeoutActive);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentStep]);

  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return <WelcomeStep />;
      case 'account-type':
        return <AccountTypeStep />;
      // Individual flow
      case 'phone':
        return <PhoneStep />;
      case 'individual-declarations':
        return <IndividualDeclarationsStep />;
      case 'email':
        return <EmailStep />;
      case 'personal-info':
        return <PersonalInfoStep />;
      case 'date-of-birth':
        return <DateOfBirthStep />;
      case 'employment':
        return <EmploymentStep />;
      case 'income':
        return <IncomeStep />;
      case 'payment-destinations':
        return <PaymentDestinationsStep />;
      case 'document-upload':
        return <DocumentUploadStep />;
      case 'document-details-confirmation':
        return <DocumentDetailsConfirmationStep />;
      case 'document-signing':
        return <DocumentSigningStep />;
      case 'passcode':
        return <PasscodeStep />;
      case 'final-declarations':
        return <FinalDeclarationsStep />;
      case 'success':
        return <SuccessStep />;

      // Corporate flow
      case 'corporate-company-search':
        return <CorporateCompanySearchStep />;
      case 'corporate-company-details':
        return <CorporateCompanyDetailsStep />;
      case 'corporate-services':
        return <CorporateServicesStep />;
      case 'corporate-phone':
        return <PhoneStep />;
      case 'corporate-email':
        return <EmailStep />;
      case 'corporate-passcode':
        return <PasscodeStep />;
      case 'corporate-business-activity':
        return <CorporateBusinessActivityStep />;
      case 'corporate-payment-flows':
        return <CorporatePaymentFlowsStep />;
      case 'corporate-baas':
        return <CorporateBaaSStep />;
      case 'corporate-directors-shareholders':
        return <CorporateDirectorsShareholdersStep />;
      case 'corporate-ubo-trace':
        return <CorporateUBOTraceStep />;
      case 'corporate-authorized-person':
        return <CorporateUBOTraceStep />;
      case 'corporate-individual-declarations':
        return <CorporateUBOTraceStep />;
      case 'corporate-biometrics':
        return <CorporateBiometricsStep />;
      case 'corporate-documents':
        return <DocumentUploadStep />;
      case 'corporate-document-signing':
        return <DocumentSigningStep />;
      case 'corporate-success':
        return <SuccessStep />;

      default:
        return <WelcomeStep />;
    }
  };

  const showNavigation = currentStep !== 'welcome' && currentStep !== 'success' && currentStep !== 'corporate-success';
  const showProgressIndicator = showNavigation && currentStep !== 'account-type';
  const showLanguageSelector = currentStep === 'welcome';

  if (timedOut) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-5">
          <Clock className="w-8 h-8 text-destructive" />
        </div>
        <h1 className="text-xl font-bold text-foreground mb-2">Session Timed Out</h1>
        <p className="text-muted-foreground mb-6 max-w-sm text-sm">
          Your onboarding session expired due to inactivity. Your progress has been saved. Please log in again to continue.
        </p>
        <button
          onClick={() => { window.location.href = '/app/login'; }}
          className="px-6 py-3 rounded-full bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-all"
        >
          Log In to Resume
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative min-h-screen-safe overflow-x-visible">
      {showCountdown && (
        <SessionTimeoutCountdown
          secondsLeft={countdownSeconds}
          onStayLoggedIn={resetTimer}
          portalName="Onboarding"
        />
      )}

      {/* Identity duplicate block overlay */}
      {duplicateBlock && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/90 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border-2 border-warning bg-card p-6 space-y-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-warning/20 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h3 className="font-bold text-foreground text-lg mb-1">Duplicate Application Detected</h3>
                <p className="text-sm text-muted-foreground">{duplicateBlock.message}</p>
                {duplicateBlock.email && (
                  <p className="text-sm text-foreground mt-1">Account: {duplicateBlock.email}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {duplicateBlock.action !== 'contact_support' && (
                <button
                  onClick={() => { window.location.href = duplicateBlock.action === 'resume' ? '/app/login?resume=1' : '/app/login'; }}
                  className="w-full py-3 px-4 rounded-xl bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-all flex items-center justify-center gap-2"
                >
                  {duplicateBlock.action === 'resume' ? 'Resume Existing Application' : 'Log in to your account'}
                </button>
              )}
              {duplicateBlock.action === 'contact_support' && (
                <a href="mailto:support@paynovus.com" className="w-full py-3 px-4 rounded-xl bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-all flex items-center justify-center gap-2 text-center">
                  Contact Support
                </a>
              )}
              <button
                onClick={clearDuplicateBlock}
                className="w-full py-3 px-4 rounded-xl border border-border text-foreground font-medium hover:bg-secondary transition-all text-sm"
              >
                Go back
              </button>
            </div>
          </div>
        </div>
      )}

      {showLanguageSelector && (
        <div className="fixed top-4 right-4 z-[60]">
          <LanguageSelector />
        </div>
      )}

      {showNavigation && (
        <div className="onboarding-header">
          <div className="relative flex items-center">
            <BackButton />
            {showProgressIndicator && <ProgressIndicator />}
            {/* Right-side controls — only shown once past pre-passcode steps */}
            {!PRE_PASSCODE_STEPS.has(currentStep) && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <button
                  onClick={() => setSupportOpen(true)}
                  title="Help & Support"
                  className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
                <OnboardingNotificationBell />
                <button
                  onClick={async () => {
                    await supabase.auth.signOut();
                    window.location.href = '/app/onboard';
                  }}
                  title="Sign out"
                  className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {renderStep()}
      <OnboardingSupportPanel open={supportOpen} onOpenChange={setSupportOpen} />
    </div>
  );
});

export function OnboardingFlow() {
  return (
    <OnboardingProvider>
      <OnboardingContent />
    </OnboardingProvider>
  );
}
